import {
    SvcGstDtlGetFinancialTransactionConfirmationResponse,
    SvcGstDtlGetLiabilityBalanceCertificateResponse,
} from 'scbk-servicing/types/api';
import { formatUtils } from 'scbk-shared-module';

/**
 * 금융거래확인서 전자문서 데이터포맷. (국문만 있음.)
 * @param lang : '1' (1:국문, 2:영문)
 * @param data : SvcCusCinGetFinancialTransactResponse
 * @return SvcCusCinGetFinancialTransactResponse
 */
export const fmtEdocFinancialTransact = (
    lang: '1',
    data: SvcGstDtlGetFinancialTransactionConfirmationResponse,
) => {
    if (!data || !data.issueNo || !lang) return data;

    let result = { ...data }; //전달할 데이터.

    result.issueNo = fmtStrUtil('I', data.issueNo); //발급번호
    result.issueBaseDt = formatUtils.toDate(data.issueBaseDt, '.'); //발급기준일

    //발급구분(1:주민번호, 2:법인번호, 3:사업자번호)
    if (data.corpMkcd === '3') {
        result.ssnTaxId = fmtStrUtil('S', data.ssnTaxId); //주민사업자번호
    } else {
        result.ssnTaxId = fmtStrUtil('J', data.ssnTaxId); //주민사업자번호
    }

    result.unsettledBillCnt = formatUtils.formatCommas(data.unsettledBillCnt); //미결제 어음수
    result.unsettledCheckCnt = formatUtils.formatCommas(data.unsettledCheckCnt); //미결제 수표수
    result.firstdDt = formatUtils.toDate(data.firstdDt, '.'); //1차부도일
    result.secondDt = formatUtils.toDate(data.secondDt, '.'); //1차부도일
    result.delayReckonedDate = formatUtils.toDate(data.delayReckonedDate, '.'); //기산일

    //반복부(대출정보) - 대출금거래현황
    result.loanSel = data.loanSel?.map((item) => {
        item.loanCamt = formatUtils.formatCommas(item.loanCamt); //당초차입금액
        item.loanBalance = formatUtils.formatCommas(item.loanBalance); //잔액
        item.convBalance = formatUtils.formatCommas(item.convBalance); //환산잔액
        item.loanDate = formatUtils.toDate(item.loanDate, '.'); // 당초차입일자
        item.loanExpiraDt = formatUtils.toDate(item.loanExpiraDt, '.'); // 대출기한

        return item;
    });

    //반복부(담보정보) - 담보내용출력
    result.clltrlSel = data.clltrlSel?.map((item) => {
        item.quantity = formatUtils.formatCommas(item.quantity); // 수량
        item.estimateDate = formatUtils.toDate(item.estimateDate, '.'); // 감정일자

        //채무자와관계
        if (item.relationCode === '11') {
            item.relationCode = '본인';
        } else if (item.relationCode === '18') {
            item.relationCode = '기타';
        } else {
            item.relationCode = '';
        }
        //담보형태
        if (item.gageType === '01') {
            item.gageType = '정규담보';
        } else if (item.gageType === '02') {
            item.gageType = '견질담보';
        } else if (item.gageType === '03') {
            item.gageType = '후취담보';
        } else if (item.gageType === '04') {
            item.gageType = '양도담보';
        } else {
            item.gageType = '';
        }

        return item;
    });

    //반복부(카드정보) - 최근6개월이내 당좌 및 신용카드 결제내용
    result.cardSel = data.cardSel?.map((item) => {
        item.paymentCount = formatUtils.formatCommas(item.paymentCount); // 당좌결제매수
        item.paymentAmt = formatUtils.formatCommas(item.paymentAmt); // 당좌결제금액
        item.cardcashAmt = formatUtils.formatCommas(item.cardcashAmt); //카드 현금서비스금액
        item.cardpurAmt = formatUtils.formatCommas(item.cardpurAmt); //카드 물품구매금액
        item.totalAmt = formatUtils.formatCommas(item.totalAmt); //합계

        return item;
    });

    //반복부(연체정보) - 최근3개월이내 10일 이상 계속된 연체명세
    result.dlqSel = data.dlqSel?.map((item) => {
        item.delayAmt = formatUtils.formatCommas(item.delayAmt); // 연체 원금
        item.delayIntAmt = formatUtils.formatCommas(item.delayIntAmt); // 연체 이자
        item.delayDt = formatUtils.toDate(item.delayDt, '.'); // 연체발생일
        item.delayClearDt = formatUtils.toDate(item.delayClearDt, '.'); // 연체정리일

        return item;
    });

    return result;
};

/**
 * 부채잔액증명서 전자문서 데이터포맷.
 * @param lang : '1' | '2' (1:국문, 2:영문)
 * @param data : SvcCusCinGetDebtBalanceResponse
 * @return SvcCusCinGetDebtBalanceResponse
 */
export const fmtEdocDebtBalance = (
    lang: '1' | '2',
    data: SvcGstDtlGetLiabilityBalanceCertificateResponse,
) => {
    if (!data || !data.issueNo) return data;

    let result = { ...data }; //전달할 데이터.

    result.issueNo = fmtStrUtil('I', data.issueNo); //발급번호
    //국문증명서 일때만 사용.
    if (lang === '1') {
        result.issueBaseDt = formatUtils.toDateType2(result.issueBaseDt, 'ko'); // 발급기준일 yyyy년 MM월 dd일
        result.totLoanAmt = formatUtils.formatCommas(normalizeAmount(data.totLoanAmt)); // 대출잔액
        result.nonStlmtAmt = formatUtils.formatCommas(normalizeAmount(data.nonStlmtAmt)); // 미결제타점권
        result.totApprvlAmt = formatUtils.formatCommas(normalizeAmount(data.totApprvlAmt)); // 한도금액
    }
    //영문증명서 일때만 사용.
    else {
        result.issueBaseDt = fmtDatEenUs(result.issueBaseDt); // 발급기준일
        result.totLoanAmt = formatUtils.formatCurrency(Number(data.totLoanAmt), 'KRW'); // 대출잔액 (앞에 ₩표기 : ₩10,000 )
        result.totApprvlAmt = formatUtils.formatCurrency(Number(data.totApprvlAmt), 'KRW'); // 한도금액 (앞에 ₩표기 : ₩10,000 )

        result.totLoanAmtUSD = formatUtils.formatCommas(
            formatUtils.DISPLAY_LOAN_RATE2(data.totLoanAmtUSD ?? '', 2).replace('%', ''),
        ); // 대출잔액 (USD)
        result.totApprvlAmtUSD = formatUtils.formatCommas(
            formatUtils.DISPLAY_LOAN_RATE2(data.totApprvlAmtUSD ?? '', 2).replace('%', ''),
        ); // 한도금액(USD)
        result.usdExchRate = formatUtils.formatCommas(
            formatUtils.DISPLAY_LOAN_RATE2(data.usdExchRate ?? '', 2).replace('%', ''),
        ); // 외화환율
    }

    //생년월일/사업자번호
    if (data.userSsn.length === 8) {
        result.userSsn = formatUtils.toDateType2(result.userSsn, 'ko'); //yyyy년 MM월 dd일
    } else {
        result.userSsn = fmtStrUtil('S', data.userSsn); //사업자번호 포맷팅
    }

    // 반복부(부채정보)
    result.lbltySel = data.lbltySel?.map((item) => {
        item.lbltyAcctNo = formatUtils.toAccount('23', item.lbltyAcctNo); // 부채계좌번호
        item.loanRegistDt = formatUtils.toDate(item.loanRegistDt, '.'); // 대출일자
        item.loanDeadlnDt = formatUtils.toDate(item.loanDeadlnDt, '.'); // 대출기한

        //국문증명서 일때만 사용.
        if (lang === '1') {
            item.apprvlAmt = normalizeAmount(item.apprvlAmt); // 한도금액
            item.loanAmt = normalizeAmount(item.loanAmt); // 대출잔액
            item.nonStlmtAmt = normalizeAmount(item.nonStlmtAmt); // 미결제타점권

            //연체구분
            if (item.dlnqCd === 'Y') {
                item.dlnqCd = '유';
            } else {
                item.dlnqCd = '무';
            }
        }
        //영문증명서 일때만 사용.
        else {
            item.apprvlAmt = formatUtils.formatCurrency(Number(item.apprvlAmt), 'KRW'); // 한도금액 (앞에 ₩표기 : ₩10,000 )
            item.loanAmt = formatUtils.formatCurrency(Number(item.loanAmt), 'KRW'); // 대출잔액 (앞에 ₩표기 : ₩10,000 )
            item.apprvlAmtUSD = formatUtils.formatCommas(
                formatUtils.DISPLAY_LOAN_RATE2(item.apprvlAmtUSD ?? '', 2).replace('%', ''),
            ); // 한도금액(USD)
            item.loanAmtUSD = formatUtils.formatCommas(
                formatUtils.DISPLAY_LOAN_RATE2(item.loanAmtUSD ?? '', 2).replace('%', ''),
            ); // 대출잔액 (USD)

            //연체구분 (1-Operation, 2-Equipments, 3-'')
            if (item.dlnqCd === '1') {
                item.dlnqCd = 'Operation';
            } else if (item.dlnqCd === '2') {
                item.dlnqCd = 'Equipments';
            } else {
                item.dlnqCd = '';
            }
        }
        return item;
    });

    return result;
};

/**
 * 영문증명서 날짜 데이터포맷.
 * @param str : YYYYMMDD (예:20260102) 문자열.
 * @return string : JAN.02,2026
 */
const fmtDatEenUs = (str: string) => {
    if (!str || str.length != 8) return str;

    const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });

    const result = formatter
        .format(new Date(formatUtils.toDate(str, '-')))
        .toUpperCase()
        .replace(/[\s\u00A0]/g, '.')
        .replace(/,\./g, ',');

    return result;
};

/**
 * 증명서 데이터포맷.
 * @param str : 포맷팅 할 데이터.
 * @param type : I:발급번호, J:주민번호, S:사업자번호
 * @return string : 포맷팅 된 데이터.
 */
const fmtStrUtil = (type: string, str: string) => {
    if (!type || !str || str.length < 8) return str;

    let result = '';

    if ('I' === type) {
        result = str.replace(/(\d{8})(\d{3})(\d{5})/, '$1-$2-$3'); // 발급번호 00000000-000-00000
    } else if ('S' === type) {
        result = str.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3'); //사업자번호 000-00-00000
    } else if ('J' === type) {
        result = str.replace(/(\d{6})(\d{7})/, '$1-$2'); //주민자번호 000000-0000000
    }

    return result;
};

/**
 * 증명서 금액 데이터포맷.
 * @param value : 포맷팅 할 데이터.
 */
const normalizeAmount = (value?: string): string => {
    const num = Number((value ?? '').replace(/,/g, ''));
    return Number.isNaN(num) ? '0' : String(num);
};
